import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import FormInput, { FormTextArea } from "pages/FormInput";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addCivilCaseAppeal } from "services/civilCaseComplaintDetailService";
import { editAppeal2 } from "services/complaintDetailService";
import { editComplaintReception } from "services/complaintService";
import { CivilCaseAppeald, CivilCaseComplaint } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

const defaultData: CivilCaseAppeald = {
  id: undefined,
  parent_appeal_id: undefined,
  appeal_document: null,
  resolution_file: null,
  status: "pending",
  appeal_date: undefined,
  lower_org_unit: "",
  lower_admin_unit: "",
  appeal_summary: "",
  resolution_text: "",
  resolution_date: undefined,
  resolved_by: "",
  resolved_by_user_id: "",
  admin_unit_id: "",
  org_unit_id: "",
  departement_id: "",
  parent_appeal: undefined,
};

type Props = {
  complaintId?: number | string;
  compliant?: CivilCaseComplaint;
  appeal?: CivilCaseAppeald;
  appealId?: number | string;
  onClose: (refetch: boolean) => void;
};

const CivilCaseAppealRequestForm: React.FC<Props> = ({
  complaintId,
  compliant,
  appeal,
  appealId,
  onClose,
}) => {
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [formState, setFormState] = useState<CivilCaseAppeald>(defaultData);
  const [loading, setLoading] = useState(false);

  // const fetchStructure = async (admin_unit_id: string, org_unit_id: string) => {
  //   const res = await adminOrgStructure(admin_unit_id, org_unit_id);
  //   console.log(res);
  //   if (res) {
  //     setFormState((prev) => ({
  //       ...prev,
  //       org_unit_id:
  //         res?.org_unit?.parent_unit !== undefined &&
  //         res?.org_unit?.parent_unit !== null
  //           ? String(res?.org_unit?.parent_unit)
  //           : "",
  //     }));
  //   }
  // };

  // useEffect(() => {
  //   if (compliant) {
  //     fetchStructure(
  //       compliant?.admin_unit_id ?? "",
  //       compliant?.org_unit_id ?? "",
  //     );
  //   }
  // }, [compliant]);

  useEffect(() => {
    if (loginData) {
      setFormState((prev) => ({
        ...prev,
        org_unit_id: String(loginData.org_unit),
      }));
    }
  }, [loginData]);

  // File handlers
  const handleFileChange = (name: string, file: File | null) => {
    setFormState((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const validateForm = () => {
    if (!complaintId && !appealId) return "Complaint or Appeal is Required.";
    if (!formState.appeal_date) return "Appeal Date is Required.";
    if (new Date(formState?.appeal_date) > new Date()) {
      return `Appeal Date must be on or before ${new Date().toLocaleDateString()}`;
    }
    if (
      formState?.resolution_date &&
      new Date(formState?.resolution_date) > new Date()
    ) {
      return `Resolution Date must be on or before ${new Date().toLocaleDateString()}`;
    }
    if (!formState.appeal_summary) return "Appeal Claim Summary is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    // const complaint_id = complaintId
    //   ? complaintId
    //   : appealId
    //     ? undefined
    //     : formState.complaint_id;

    addCivilCaseAppeal({
      ...formState,
      status: "pending",
      resolved_by_user_id: loginData?.user_id,
      resolved_by: loginData?.full_name,
      // org_unit_id: loginData?.org_unit,
      admin_unit_id: loginData?.admin_unit,
      departement_id: loginData?.structure?.org_unit?.uu_id,
      // lower_org_unit: loginData?.lower_org_unit,
      // lower_admin_unit: loginData?.lower_admin_unit,
      complaints: appealId ? undefined : complaintId,
      complaint_id: appealId ? undefined : complaintId,
      parent_appeal_id: appealId ? appealId : undefined,
      parent_appeal: appealId ? appealId : undefined,
      application_number: appeal
        ? appeal?.application_number
        : compliant?.application_number,
    })
      .then((data) => {
        if (complaintId) {
          editComplaintReception({
            id: complaintId,
            is_appeal: true,
          });
        } else if (appealId) {
          editAppeal2({
            id: appealId,
            is_appealed: true,
          });
        }

        showTooltip("Successfully Submitted!", "success");
        navigate("/civil-appeal");
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div
        className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto"
        style={{ maxHeight: "max-content" }}
      >
        {formState?.org_unit_id && (
          <form onSubmit={handleSubmit}>
            <span className="text-[24px] font-semibold ">
              Register New Appeal
            </span>

            {compliant && (
              <div className="pt-6">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-5 border border-blue-100">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                    Complaint Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-medium">
                        Application Number
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="font-semibold text-gray-900">
                          {compliant?.client_id ?? "-"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-medium">
                        Resolved By
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-semibold text-gray-900">
                          {(compliant?.resolutions &&
                            compliant?.resolutions.length > 0 &&
                            compliant?.resolutions[0]?.resolved_by) ||
                            "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-4">
              <FormInput
                label="Appeal Date *"
                type="date"
                value={
                  formState.appeal_date
                    ? new Date(formState.appeal_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                max={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
                name="appeal_date"
              />
              <FormInput
                label="Resolution Date"
                type="date"
                value={
                  formState.resolution_date
                    ? new Date(formState.resolution_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                max={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
                name="resolution_date"
              />
            </div>

            <div className="flex flex-col gap-4 mt-4">
              <FormTextArea
                label="Appeal Claim Summary *"
                value={formState.appeal_summary || ""}
                onChange={handleChange}
                name="appeal_summary"
                placeholder="Enter appeal claim summary"
                rows={4}
              />
              <FormTextArea
                label="Lower Administration Unit Resolution"
                value={formState.resolution_text || ""}
                onChange={handleChange}
                name="resolution_text"
                placeholder="Enter lower administration unit resolution"
                rows={4}
              />
              <FileUpload
                label="Appeal Claim Document"
                fileTypes="*"
                info="Upload Files"
                required={false}
                onChange={(file) => handleFileChange("appeal_document", file)}
              />
              <FileUpload
                label="Lower Administration Unit Resolution Document"
                fileTypes="*"
                info="Upload Files"
                required={false}
                onChange={(file) => handleFileChange("resolution_file", file)}
              />
            </div>
            <div className="flex flex-row mt-4 gap-3">
              <button
                type="reset"
                disabled={loading}
                className="flex-1 border border-[#212121] py-3 text-[#747474]  cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 items-center bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex justify-center"
                value={"Submit Appeal"}
              >
                {loading ? (
                  <>
                    <span className="animate-spin">{LoadingIcon}</span>
                    Processing...
                  </>
                ) : (
                  "Submit Appeal"
                )}
              </button>
            </div>
          </form>
        )}
        {/* {!formState?.org_unit_id && (
          <div className="mb-4 flex flex-col">
            <span className="text-[24px] font-semibold ">
              Register New Appeal
            </span>
            Change admin unit to request appeal
          </div>
        )} */}
      </div>
    </div>
  );
};

export default CivilCaseAppealRequestForm;
